import React from 'react';
import './Install.css';

const Install = () => {
    return (
        <div className="install-page">
            {/* Hero Section */}
            <section className="hero">
                <div className="container">
                    <h1 className="hero-title">
                        Get Started with
                        <span className="gradient-text"> Terra Code CLI</span>
                    </h1>
                    <p className="hero-description">
                        🚀 FREE BETA - Limited Time! Terra Code CLI is completely free for all users during our beta phase. Get started now and experience the future of AI-powered development!
                    </p>
                    <div className="hero-buttons">
                        <a href="#installation" className="btn btn-primary">Install Now</a>
                        <a href="https://github.com/TerraAGI/terra-code-cli" className="btn btn-secondary" target="_blank" rel="noopener noreferrer">
                            View on GitHub
                        </a>
                    </div>
                </div>
            </section>

            {/* Installation Section */}
            <section id="installation" className="section">
                <div className="container">
                    <h2 className="section-title">Installation</h2>

                    <div className="install-section">
                        <h3>Prerequisites</h3>
                        <p>Ensure you have Node.js version 20 or higher installed.</p>
                        <div className="code-block">
                            <code>curl -qL https://www.npmjs.com/install.sh | sh</code>
                        </div>
                    </div>

                    <div className="install-section">
                        <h3>Install from npm</h3>
                        <div className="code-block">
                            <code>npm install -g @terra-code/terra-code@latest</code>
                            <code>terra --version</code>
                        </div>
                    </div>

                    <div className="install-section">
                        <h3>Install from source</h3>
                        <div className="code-block">
                            <code>git clone https://github.com/TerraAGI/terra-code-cli.git</code>
                            <code>cd terra-code-cli</code>
                            <code>npm install</code>
                            <code>npm install -g .</code>
                        </div>
                    </div>
                </div>
            </section>

            {/* Quick Start Section */}
            <section className="section">
                <div className="container">
                    <h2 className="section-title">Quick Start</h2>

                    <div className="install-section">
                        <h3>Basic Usage</h3>
                        <div className="code-block">
                            <code># Start Terra Code CLI</code>
                            <code>terra</code>
                            <code></code>
                            <code># Example commands</code>
                            <code>&gt; Explain this codebase structure</code>
                            <code>&gt; Help me refactor this function</code>
                            <code>&gt; Generate unit tests for this module</code>
                        </div>
                    </div>

                    <div className="install-section">
                        <h3>Development Brain</h3>
                        <div className="code-block">
                            <code># Upload documents to your brain (supports .txt, .md, .docx, .pdf)</code>
                            <code>terra&gt; /brain upload online_order_flow.md</code>
                            <code>✅ Document uploaded successfully to your Terra brain</code>
                            <code></code>
                            <code># Interactive Knowledge Transfer sessions</code>
                            <code>terra&gt; /brain kt</code>
                            <code>🧠 Starting Knowledge Transfer session...</code>
                            <code></code>
                            <code># Remember personal facts</code>
                            <code>terra&gt; /brain remember "I prefer TypeScript over JavaScript"</code>
                            <code>🧠 Remembering: "I prefer TypeScript over JavaScript"</code>
                        </div>
                    </div>
                </div>
            </section>

            {/* Authorization Section */}
            <section className="section">
                <div className="container">
                    <h2 className="section-title">Authorization</h2>

                    <div className="install-section">
                        <h3>🚀 Qwen OAuth (Recommended - Start in 30 seconds)</h3>
                        <p>The easiest way to get started - completely free with generous quotas:</p>
                        <div className="code-block">
                            <code># Just run this command and follow the browser authentication</code>
                            <code>terra</code>
                        </div>
                        <div className="feature-card">
                            <h4>Free Tier Benefits:</h4>
                            <ul>
                                <li>✅ 2,000 requests/day (no token counting needed)</li>
                                <li>✅ 60 requests/minute rate limit</li>
                                <li>✅ Automatic credential refresh</li>
                                <li>✅ Zero cost for individual users</li>
                            </ul>
                        </div>
                    </div>

                    <div className="install-section">
                        <h3>OpenAI-Compatible API</h3>
                        <p>Use API keys for OpenAI or other compatible providers:</p>

                        <h4>User Settings File (Recommended)</h4>
                        <p>Add to <code>~/.terra/settings.json</code>:</p>
                        <div className="code-block">
                            <code>{'{'}</code>
                            <code>  "openaiApiKey": "your_api_key_here",</code>
                            <code>  "openaiBaseUrl": "your_api_endpoint",</code>
                            <code>  "openaiModel": "your_model_choice"</code>
                            <code>{'}'}</code>
                        </div>

                        <h4>Environment Variables</h4>
                        <div className="code-block">
                            <code>export OPENAI_API_KEY="your_api_key_here"</code>
                            <code>export OPENAI_BASE_URL="your_api_endpoint"</code>
                            <code>export OPENAI_MODEL="your_model_choice"</code>
                        </div>
                    </div>
                </div>
            </section>

            {/* Commands Section */}
            <section className="section">
                <div className="container">
                    <h2 className="section-title">Commands & Shortcuts</h2>

                    <div className="commands-grid">
                        <div className="feature-card">
                            <h3>Core Commands</h3>
                            <ul>
                                <li><code>/help</code> - Display available commands</li>
                                <li><code>/tools</code> - List available tools</li>
                                <li><code>/theme</code> - Change the CLI theme</li>
                                <li><code>/clear</code> - Clear conversation history</li>
                                <li><code>/compress</code> - Compress conversation to save tokens</li>
                                <li><code>/stats</code> - Show current session information</li>
                                <li><code>/exit</code> or <code>/quit</code> - Exit Terra Code CLI</li>
                            </ul>
                        </div>

                        <div className="feature-card">
                            <h3>Brain Commands (Priority)</h3>
                            <ul>
                                <li><code>/brain upload &lt;file&gt;</code> - Upload document to your brain</li>
                                <li><code>/brain kt</code> - Interactive Knowledge Transfer session</li>
                                <li><code>/brain remember &lt;fact&gt;</code> - Remember personal facts and preferences</li>
                            </ul>
                        </div>

                        <div className="feature-card">
                            <h3>Semantic Analysis Commands</h3>
                            <ul>
                                <li><code>/semantic index &lt;project-path&gt;</code> - Index project for semantic search</li>
                                <li><code>/semantic status</code> - Check semantic analysis status</li>
                            </ul>
                        </div>
                    </div>
                </div>
            </section>

            {/* CTA Section */}
            <section className="cta-section">
                <div className="container">
                    <div className="cta-content">
                        <h2>Ready to Get Started?</h2>
                        <p>Join the community of developers using TerraAGI to write better code faster.</p>
                        <a href="https://github.com/TerraAGI/terra-code-cli" className="btn btn-primary" target="_blank" rel="noopener noreferrer">
                            Get Started
                        </a>
                    </div>
                </div>
            </section>
        </div>
    );
};

export default Install;
